// Signal Window — editorial trading-terminal (Claude Design v2 port).
//
// Ported from the design handoff (handoff_signal_window/composer.jsx →
// SignalWindowComposer). Desktop-first per the STRIKZA design system.
//
// Layout: page header (title + live/query status) → 2-col grid:
//   main column  →  Intent · Funnel · Hero slip · Posture · Legs · Notes · Bench
//   right rail   →  Refine (applied log · quick chips · command bar)  [sticky]
//
// Truth contract (binding · brief §7/§8):
//   · "could reach", never "will reach" — every ceiling pairs with a floor.
//   · Risk notes are first-class, surfaced verbatim from the server.
//   · Methodology stays private — the wire is already sanitised
//     (confidence band only; no scores, no raw probabilities).
//
// The hook (useSignalSearch) is untouched: this file only swaps rendering.
//
// v1 scope (founder ruling): full visual build. Bench "+ swap" and the
// refine-applied "×" are present but no-op (toast) — the backend refine
// commands for swap/undo land in a follow-up.

import { useCallback, useEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";
import {
  useSignalSearch,
  type ComposedSlip,
  type SignalLeg,
  type SignalSearchResult,
  type WindowKind,
} from "../../signals/useSignalSearch.ts";

// ─── Design tokens (mirror data.jsx `SW`, 1:1 with STRIKZA_DESIGN_SYSTEM) ──
const SW = {
  bg: "#0A0A0A",
  surface: "#111111",
  surface2: "#161616",
  border: "#1A1A1A",
  borderBright: "#242424",
  borderHi: "#303030",
  accent: "#D4A843",
  accentDim: "#8A6E2A",
  accentInk: "rgba(212,168,67,0.08)",
  text: "#F0EDE6",
  textSec: "#9B9790",
  textMuted: "#5C5A57",
  textDim: "#3A3835",
  good: "#5DBF8A",
  goodInk: "rgba(46,125,82,0.12)",
  warn: "#BF9D5D",
  warnInk: "rgba(125,94,46,0.12)",
  danger: "#BF5D5D",
  dangerInk: "rgba(125,46,46,0.12)",
  serif: '"Playfair Display", Georgia, serif',
  sans: '"DM Sans", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, monospace',
} as const;

const SP = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 24, 6: 32, 7: 48, 8: 64 } as const;
const RAIL_W = 360;

type Posture = "safer" | "current" | "aggressive";

const WINDOW_OPTIONS: { key: WindowKind; label: string; hint: string }[] = [
  { key: "today", label: "Today", hint: "tonight" },
  { key: "tomorrow", label: "Tomorrow", hint: "1 day" },
  { key: "weekend", label: "Weekend", hint: "Fri–Sun" },
  { key: "7d", label: "7 days", hint: "next week" },
  { key: "month", label: "Month", hint: "~14d real" },
  { key: "custom", label: "Custom", hint: "pick" },
];

const TARGET_TICKS = [1.5, 2.0, 3.0, 5.0, 8.0, 15.0, 30.0];

const QUICK_CMDS = [
  "make it safer",
  "raise odds",
  "lower odds",
  "more options",
  "avoid away teams",
  "weekend only",
  "cap 3 legs",
];

// ─── Helpers ───────────────────────────────────────────────────────
function fmtKickoff(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const wd = d.toLocaleDateString(undefined, { weekday: "short" });
  const t = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${wd} · ${t}`;
}

function marketLabel(code: string): string {
  const c = (code || "").toLowerCase();
  if (c === "1x2" || c === "match_winner") return "1X2";
  if (c === "btts" || c.includes("both_teams")) return "BTTS";
  if (c === "ou" || c === "over_under" || c === "total_goals") return "O/U";
  const ou = c.match(/^total_goals_(\d+)_(\d+)$/);
  if (ou) return `O/U ${ou[1]}.${ou[2]}`;
  const ah = c.match(/^asian_handicap_(plus|minus)_(\d+)(?:_(\d+))?$/);
  if (ah) {
    const sign = ah[1] === "minus" ? "−" : "+";
    const dec = ah[3] ? `.${ah[3]}` : "";
    return `AH ${sign}${ah[2]}${dec}`;
  }
  return (code || "").toUpperCase().replace(/_/g, " ");
}

function selectionLabel(leg: SignalLeg): string {
  const s = (leg.selection || "").toLowerCase();
  if (s === "home") return leg.home_team;
  if (s === "away") return leg.away_team;
  if (s === "draw") return "Draw";
  return leg.selection ? leg.selection.charAt(0).toUpperCase() + leg.selection.slice(1) : "—";
}

/** Merge slip risk_notes + top-level notes, de-duplicated, order preserved. */
function mergeNotes(slip: ComposedSlip | null, result: SignalSearchResult | null): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const n of [...(slip?.risk_notes ?? []), ...(result?.notes ?? [])]) {
    const k = n.trim();
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(n);
    }
  }
  return out;
}

function pickSlip(result: SignalSearchResult | null, posture: Posture): ComposedSlip | null {
  if (!result) return null;
  if (posture === "safer") return result.alternatives?.safer ?? result.slip;
  if (posture === "aggressive") return result.alternatives?.aggressive ?? result.slip;
  return result.slip;
}

// ─── Primitives ────────────────────────────────────────────────────
function Eyebrow({
  children,
  tone = "muted",
  line = false,
  size = 11,
}: {
  children: React.ReactNode;
  tone?: "muted" | "amber" | "text";
  line?: boolean;
  size?: number;
}): JSX.Element {
  const color = tone === "amber" ? SW.accent : tone === "text" ? SW.text : SW.textMuted;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: SP[3],
        fontFamily: SW.mono,
        fontSize: size,
        fontWeight: 500,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {line && <span style={{ width: 22, height: 1, background: color, flexShrink: 0 }} />}
      {children}
    </span>
  );
}

function Pill({
  tone = "default",
  children,
  testid,
}: {
  tone?: "default" | "amber" | "good" | "warn" | "danger";
  children: React.ReactNode;
  testid?: string;
}): JSX.Element {
  const tones = {
    default: { c: SW.textSec, b: SW.borderBright, bg: "transparent" },
    amber: { c: SW.accent, b: SW.accentDim, bg: SW.accentInk },
    good: { c: SW.good, b: "rgba(46,125,82,0.4)", bg: SW.goodInk },
    warn: { c: SW.warn, b: "rgba(125,94,46,0.6)", bg: SW.warnInk },
    danger: { c: SW.danger, b: "rgba(125,46,46,0.6)", bg: SW.dangerInk },
  } as const;
  const t = tones[tone];
  return (
    <span
      data-testid={testid}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: SP[2],
        padding: "5px 10px",
        borderRadius: 2,
        background: t.bg,
        color: t.c,
        border: `1px solid ${t.b}`,
        fontFamily: SW.mono,
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Conf({ high }: { high: boolean }): JSX.Element {
  return high ? (
    <span
      title="High confidence"
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: SW.accent,
        boxShadow: `0 0 6px ${SW.accent}66`,
        display: "inline-block",
      }}
    />
  ) : (
    <span
      title="Moderate confidence"
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        border: `1px solid ${SW.textMuted}`,
        display: "inline-block",
      }}
    />
  );
}

const cardStyle: CSSProperties = {
  background: SW.surface,
  border: `1px solid ${SW.borderBright}`,
  borderRadius: 8,
};

// ─── Page header (folds CwTopBar status — no second topbar) ─────────
function PageHeader({ queryId, live }: { queryId?: string; live: boolean }): JSX.Element {
  return (
    <header
      data-testid="signals-topbar"
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: SP[5],
        flexWrap: "wrap",
        marginBottom: SP[6],
      }}
    >
      <div>
        <Eyebrow tone="amber" line>
          Compose
        </Eyebrow>
        <h1
          data-testid="signals-page-title"
          style={{
            margin: `${SP[3]}px 0 0`,
            fontFamily: SW.serif,
            fontWeight: 500,
            fontSize: 44,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            color: SW.text,
            maxWidth: 820,
          }}
        >
          Set a window. Name a target.{" "}
          <span style={{ color: SW.textMuted, fontStyle: "italic" }}>We compose the safest reach.</span>
        </h1>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: SP[4], whiteSpace: "nowrap" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: SP[2],
            fontFamily: SW.mono,
            fontSize: 10.5,
            letterSpacing: "0.2em",
            color: SW.textMuted,
            textTransform: "uppercase",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: live ? SW.accent : SW.good,
              animation: "sw-pulse 2s ease-in-out infinite",
              boxShadow: `0 0 6px ${live ? SW.accent : SW.good}`,
            }}
          />
          {live ? "Composing" : "Prices fresh"}
        </span>
        {queryId && (
          <>
            <span style={{ width: 1, height: 16, background: SW.borderBright }} />
            <span
              data-testid="signals-query-id"
              style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textDim, letterSpacing: "0.14em" }}
            >
              QUERY #{queryId.slice(0, 8)}
            </span>
          </>
        )}
      </div>
    </header>
  );
}

// ─── Intent bar ────────────────────────────────────────────────────
function IntentBar({
  windowKey,
  target,
  maxLegs,
  dimmed,
  onWindow,
  onTargetDrag,
  onTargetCommit,
  onMaxLegs,
  onRecompose,
}: {
  windowKey: WindowKind;
  target: number;
  maxLegs: number;
  dimmed: boolean;
  onWindow: (k: WindowKind) => void;
  onTargetDrag: (v: number) => void;
  onTargetCommit: (v: number) => void;
  onMaxLegs: (delta: number) => void;
  onRecompose: () => void;
}): JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const targetIdx = TARGET_TICKS.reduce(
    (best, v, i) => (Math.abs(v - target) < Math.abs((TARGET_TICKS[best] ?? 0) - target) ? i : best),
    0,
  );
  const targetPct = (targetIdx / (TARGET_TICKS.length - 1)) * 100;

  const valueFromX = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return target;
    const r = el.getBoundingClientRect();
    const frac = Math.min(Math.max((clientX - r.left) / r.width, 0), 1);
    const idx = Math.round(frac * (TARGET_TICKS.length - 1));
    return TARGET_TICKS[idx] ?? target;
  };

  const label: CSSProperties = {
    fontFamily: SW.mono,
    fontSize: 10,
    color: SW.textMuted,
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    fontWeight: 500,
    marginBottom: SP[3],
  };

  return (
    <section
      data-testid="signals-intent"
      style={{
        ...cardStyle,
        marginBottom: SP[6],
        opacity: dimmed ? 0.5 : 1,
        pointerEvents: dimmed ? "none" : "auto",
        transition: "opacity .25s ease",
      }}
    >
      {/* Row 1 — window */}
      <div data-testid="signals-window" style={{ padding: `${SP[5]}px ${SP[5]}px ${SP[4]}px`, borderBottom: `1px solid ${SW.border}` }}>
        <div style={label}>Window</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: SP[2] }}>
          {WINDOW_OPTIONS.map((opt) => {
            const active = opt.key === windowKey;
            return (
              <button
                key={opt.key}
                type="button"
                data-testid={`signals-window-${opt.key}`}
                onClick={() => onWindow(opt.key)}
                style={{
                  padding: "10px 14px",
                  background: active ? SW.accentInk : "transparent",
                  border: `1px solid ${active ? SW.accent : SW.borderBright}`,
                  borderRadius: 4,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: SP[2],
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontFamily: SW.sans, fontSize: 13, fontWeight: 500, color: active ? SW.accent : SW.text }}>
                  {opt.label}
                </span>
                <span
                  style={{
                    fontFamily: SW.mono,
                    fontSize: 9,
                    color: SW.textMuted,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  {opt.hint}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 2 — target / max legs / recompose */}
      <div
        style={{
          padding: SP[5],
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 200px auto",
          gap: SP[6],
          alignItems: "flex-end",
        }}
      >
        {/* Target */}
        <div data-testid="signals-target">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: SP[3] }}>
            <span style={label}>Target</span>
            <span
              data-testid="signals-target-value"
              style={{
                fontFamily: SW.serif,
                fontStyle: "italic",
                fontSize: 22,
                fontWeight: 500,
                color: SW.accent,
                lineHeight: 1,
              }}
            >
              ~{target.toFixed(1)}×
            </span>
          </div>
          <div
            ref={trackRef}
            data-testid="signals-target-track"
            role="slider"
            aria-valuemin={TARGET_TICKS[0]}
            aria-valuemax={TARGET_TICKS[TARGET_TICKS.length - 1]}
            aria-valuenow={target}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              dragging.current = true;
              onTargetDrag(valueFromX(e.clientX));
            }}
            onPointerMove={(e) => {
              if (dragging.current) onTargetDrag(valueFromX(e.clientX));
            }}
            onPointerUp={(e) => {
              dragging.current = false;
              try {
                e.currentTarget.releasePointerCapture(e.pointerId);
              } catch {
                /* noop */
              }
              onTargetCommit(valueFromX(e.clientX));
            }}
            style={{ position: "relative", height: 26, cursor: "pointer", touchAction: "none" }}
          >
            <div style={{ position: "absolute", left: 0, right: 0, top: 12, height: 1, background: SW.borderBright, pointerEvents: "none" }} />
            <div style={{ position: "absolute", left: 0, top: 12, width: `${targetPct}%`, height: 1, background: SW.accent, pointerEvents: "none" }} />
            {TARGET_TICKS.map((v, i) => (
              <span
                key={v}
                style={{
                  position: "absolute",
                  left: `${(i / (TARGET_TICKS.length - 1)) * 100}%`,
                  top: 6,
                  transform: "translateX(-50%)",
                  width: 1,
                  height: i === targetIdx ? 14 : 9,
                  background: i <= targetIdx ? SW.accent : SW.borderHi,
                  pointerEvents: "none",
                }}
              />
            ))}
            <div
              style={{
                position: "absolute",
                left: `${targetPct}%`,
                top: 7,
                transform: "translateX(-50%)",
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: SW.accent,
                boxShadow: `0 0 12px ${SW.accent}`,
                pointerEvents: "none",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: SP[1],
              fontFamily: SW.mono,
              fontSize: 9,
              letterSpacing: "0.08em",
            }}
          >
            {TARGET_TICKS.map((v, i) => (
              <span key={v} style={{ color: i === targetIdx ? SW.accent : SW.textMuted }}>
                {v}×
              </span>
            ))}
          </div>
        </div>

        {/* Max legs */}
        <div data-testid="signals-max-legs">
          <div style={label}>Max legs</div>
          <div style={{ display: "flex", alignItems: "stretch", border: `1px solid ${SW.borderBright}`, borderRadius: 4, overflow: "hidden" }}>
            <button
              type="button"
              data-testid="signals-max-legs-decrement"
              onClick={() => onMaxLegs(-1)}
              style={{ padding: `${SP[2]}px ${SP[4]}px`, background: "transparent", border: "none", fontFamily: SW.mono, fontSize: 16, color: SW.textSec, cursor: "pointer" }}
            >
              −
            </button>
            <span
              data-testid="signals-max-legs-value"
              style={{
                flex: 1,
                padding: `${SP[2]}px 0`,
                borderLeft: `1px solid ${SW.borderBright}`,
                borderRight: `1px solid ${SW.borderBright}`,
                fontFamily: SW.serif,
                fontSize: 20,
                fontWeight: 500,
                color: SW.text,
                textAlign: "center",
              }}
            >
              {maxLegs}
            </span>
            <button
              type="button"
              data-testid="signals-max-legs-increment"
              onClick={() => onMaxLegs(1)}
              style={{ padding: `${SP[2]}px ${SP[4]}px`, background: "transparent", border: "none", fontFamily: SW.mono, fontSize: 16, color: SW.textSec, cursor: "pointer" }}
            >
              +
            </button>
          </div>
        </div>

        {/* Recompose */}
        <button
          type="button"
          data-testid="signals-recompose"
          onClick={onRecompose}
          style={{
            padding: "13px 28px",
            background: SW.accent,
            color: SW.bg,
            border: "none",
            borderRadius: 4,
            fontFamily: SW.sans,
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Recompose <span style={{ marginLeft: 6 }}>→</span>
        </button>
      </div>
    </section>
  );
}

// ─── Funnel strip ──────────────────────────────────────────────────
function FunnelStrip({
  scanned,
  qualified,
  selected,
  rejected,
  target,
}: {
  scanned: number;
  qualified: number;
  selected: number;
  rejected: number;
  target: number;
}): JSX.Element {
  const steps = [
    { key: "scanned", label: "Scanned", v: scanned, accent: false },
    { key: "qualified", label: "Qualified", v: qualified, accent: false },
    { key: "selected", label: "Selected", v: selected, accent: selected > 0 },
  ];
  return (
    <section data-testid="signals-funnel" style={{ ...cardStyle, marginBottom: SP[6], padding: `${SP[5]}px ${SP[6]}px` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP[4], gap: SP[4], flexWrap: "wrap" }}>
        <Eyebrow tone="muted" line>
          The funnel
        </Eyebrow>
        <div
          data-testid="signals-funnel-meta"
          style={{
            display: "flex",
            alignItems: "center",
            gap: SP[4],
            fontFamily: SW.mono,
            fontSize: 10,
            color: SW.textMuted,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <span>{rejected} rejected on price</span>
          <span style={{ color: SW.textDim }}>/</span>
          <span>methodology private</span>
          <span style={{ color: SW.textDim }}>/</span>
          <span style={{ color: SW.accent }}>target ~{target.toFixed(1)}×</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr auto 1fr", alignItems: "center" }}>
        {steps.map((s, i, arr) => (
          <div key={s.key} style={{ display: "contents" }}>
            <div style={{ textAlign: "center" }}>
              <div
                data-testid={`signals-funnel-${s.key}`}
                style={{
                  fontFamily: SW.serif,
                  fontSize: 56,
                  fontWeight: 500,
                  color: s.accent ? SW.accent : SW.text,
                  lineHeight: 0.9,
                  letterSpacing: "-0.025em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {s.v}
              </div>
              <div style={{ fontFamily: SW.mono, fontSize: 10, letterSpacing: "0.22em", color: SW.textMuted, textTransform: "uppercase", marginTop: SP[3] }}>
                {s.label}
              </div>
            </div>
            {i < arr.length - 1 && (
              <div style={{ padding: `0 ${SP[5]}px` }}>
                <svg width="80" height="12" viewBox="0 0 80 12" aria-hidden="true">
                  <line x1="0" y1="6" x2="74" y2="6" stroke={SW.borderHi} strokeWidth="1" strokeDasharray="3 4" />
                  <path d="M68 1 L78 6 L68 11" fill="none" stroke={SW.borderHi} strokeWidth="1" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Hero slip ─────────────────────────────────────────────────────
function HeroSlip({
  slip,
  target,
  shortfall,
  highVariance,
  posture,
}: {
  slip: ComposedSlip;
  target: number;
  shortfall: boolean;
  highVariance: boolean;
  posture: Posture;
}): JSX.Element {
  const tone = highVariance ? "danger" : shortfall ? "warn" : "good";
  const prob = slip.estimated_probability;
  const pillText = highVariance
    ? `High variance · ${(prob * 100).toFixed(1)}% cash`
    : shortfall
      ? `Below ${target.toFixed(1)}× target · honest`
      : `Meets ${target.toFixed(1)}× target`;
  const fails = Math.round((1 - prob) * 100);
  const cash = 100 - fails;

  return (
    <section data-testid="signals-hero" style={{ ...cardStyle, borderTop: `2px solid ${SW.accent}`, marginBottom: SP[6] }}>
      <div
        style={{
          padding: `${SP[5]}px ${SP[6]}px ${SP[4]}px`,
          borderBottom: `1px solid ${SW.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: SP[4],
        }}
      >
        <Eyebrow tone="amber" line>
          The composed slip
        </Eyebrow>
        <Pill tone={tone} testid="signals-hero-status-pill">
          {pillText}
        </Pill>
      </div>

      <div style={{ padding: `${SP[7]}px ${SP[6]}px ${SP[6]}px`, display: "grid", gridTemplateColumns: "1fr 1px 1fr", gap: SP[7] }}>
        {/* Could reach */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 500 }}>
            Could reach
          </div>
          <div
            data-testid="signals-hero-combined"
            style={{
              fontFamily: SW.serif,
              fontSize: 96,
              fontWeight: 500,
              color: SW.text,
              lineHeight: 0.88,
              letterSpacing: "-0.03em",
              fontVariantNumeric: "tabular-nums",
              marginTop: SP[3],
            }}
          >
            {slip.combined_odds.toFixed(2)}
            <span style={{ color: SW.accent, fontStyle: "italic" }}>×</span>
          </div>
          <div
            style={{
              marginTop: SP[4],
              fontFamily: SW.mono,
              fontSize: 11,
              color: SW.textMuted,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: SP[3],
              flexWrap: "wrap",
            }}
          >
            {shortfall ? (
              <span data-testid="signals-hero-shortfall-meta" style={{ display: "inline-flex", gap: SP[3], alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ textDecoration: "line-through", color: SW.textDim }}>{target.toFixed(2)}× target</span>
                <span style={{ color: SW.textDim }}>·</span>
                <span>pool exhausted</span>
              </span>
            ) : (
              <span style={{ color: SW.good }}>at or above your target</span>
            )}
          </div>
        </div>

        <div style={{ width: 1, background: SW.border, justifySelf: "center" }} />

        {/* Probability */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 500 }}>
            Probability
          </div>
          <div
            data-testid="signals-hero-probability"
            style={{
              display: "flex",
              alignItems: "baseline",
              fontFamily: SW.serif,
              fontSize: 96,
              fontWeight: 500,
              color: SW.text,
              lineHeight: 0.88,
              letterSpacing: "-0.03em",
              fontVariantNumeric: "tabular-nums",
              marginTop: SP[3],
            }}
          >
            <span>{(prob * 100).toFixed(1)}</span>
            <span style={{ fontSize: 42, color: SW.textMuted, fontStyle: "italic", marginLeft: 4 }}>%</span>
          </div>
          <div
            style={{
              marginTop: SP[4],
              fontFamily: SW.mono,
              fontSize: 11,
              color: SW.textMuted,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: SP[3],
              flexWrap: "wrap",
            }}
          >
            <span>{slip.leg_count} legs</span>
            <span style={{ color: SW.textDim }}>·</span>
            <span>avg {slip.avg_leg_odds.toFixed(2)}×</span>
            <span style={{ color: SW.textDim }}>·</span>
            <span style={{ color: posture === "aggressive" ? SW.danger : posture === "safer" ? SW.good : SW.textMuted }}>{posture} posture</span>
          </div>
        </div>
      </div>

      {highVariance && (
        <div
          data-testid="signals-hero-variance-ribbon"
          style={{
            padding: `${SP[4]}px ${SP[6]}px`,
            background: SW.dangerInk,
            borderTop: "1px solid rgba(125,46,46,0.4)",
            display: "flex",
            alignItems: "center",
            gap: SP[4],
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: `1.5px solid ${SW.danger}`,
              background: "rgba(125,46,46,0.2)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: SW.danger,
              fontFamily: SW.serif,
              fontStyle: "italic",
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            !
          </span>
          <span style={{ fontFamily: SW.serif, fontStyle: "italic", fontSize: 14, color: SW.text, lineHeight: 1.5 }}>
            Roughly <span style={{ color: SW.danger, fontStyle: "normal" }}>{fails} of 100</span> tries don't cash. The {cash} that do, pay{" "}
            {slip.combined_odds.toFixed(2)}×.
          </span>
        </div>
      )}
    </section>
  );
}

// ─── Posture switch ────────────────────────────────────────────────
function PostureSwitch({
  posture,
  onChange,
  result,
}: {
  posture: Posture;
  onChange: (p: Posture) => void;
  result: SignalSearchResult;
}): JSX.Element {
  const cells: { id: Posture; label: string; slip: ComposedSlip | null | undefined; sub: string }[] = [
    { id: "safer", label: "Safer", slip: result.alternatives?.safer, sub: "lower ceiling · higher chance" },
    { id: "current", label: "Current", slip: result.slip, sub: "as composed" },
    { id: "aggressive", label: "Aggressive", slip: result.alternatives?.aggressive, sub: "higher ceiling · high variance" },
  ];
  return (
    <section data-testid="signals-posture" style={{ marginBottom: SP[6] }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP[4] }}>
        <Eyebrow tone="muted" line>
          Swap posture
        </Eyebrow>
        <span style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textDim, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          pre-composed · one tap
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: SP[3] }}>
        {cells.map((c) => {
          const active = c.id === posture;
          const avail = !!c.slip;
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`signals-posture-${c.id}`}
              disabled={!avail}
              onClick={() => avail && onChange(c.id)}
              style={{
                padding: SP[5],
                background: active ? SW.accentInk : SW.surface,
                border: `1px solid ${active ? SW.accent : SW.borderBright}`,
                borderRadius: 8,
                cursor: avail ? "pointer" : "default",
                textAlign: "left",
                opacity: avail ? 1 : 0.45,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP[3] }}>
                <span style={{ fontFamily: SW.mono, fontSize: 10.5, letterSpacing: "0.24em", color: active ? SW.accent : SW.textMuted, textTransform: "uppercase", fontWeight: 500 }}>
                  {c.label}
                </span>
                {active && <span style={{ width: 6, height: 6, borderRadius: "50%", background: SW.accent, boxShadow: `0 0 6px ${SW.accent}` }} />}
              </div>
              {avail && c.slip ? (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", gap: SP[3], fontFamily: SW.serif, fontSize: 32, fontWeight: 500, color: SW.text, lineHeight: 1, letterSpacing: "-0.015em", fontVariantNumeric: "tabular-nums" }}>
                    <span>
                      {c.slip.combined_odds.toFixed(2)}
                      <span style={{ color: SW.accent, fontStyle: "italic" }}>×</span>
                    </span>
                    <span style={{ fontSize: 16, color: SW.textMuted, fontStyle: "italic" }}>{(c.slip.estimated_probability * 100).toFixed(1)}%</span>
                  </div>
                  <div style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, marginTop: SP[3], letterSpacing: "0.14em", textTransform: "uppercase" }}>
                    {c.slip.leg_count} legs · {c.sub}
                  </div>
                </>
              ) : (
                <div style={{ fontFamily: SW.serif, fontSize: 28, color: SW.textDim }}>—</div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ─── Legs table ────────────────────────────────────────────────────
const LEG_GRID = "36px minmax(0, 1.6fr) minmax(0, 0.9fr) 110px minmax(0, 0.9fr) 64px 80px";

function LegsTable({
  legs,
  expanded,
  onToggle,
}: {
  legs: SignalLeg[];
  expanded: number;
  onToggle: (i: number) => void;
}): JSX.Element {
  return (
    <section data-testid="signals-legs" style={{ ...cardStyle, marginBottom: SP[6], overflow: "hidden" }}>
      <div style={{ padding: `${SP[4]}px ${SP[5]}px`, borderBottom: `1px solid ${SW.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Eyebrow tone="muted" line>
          Legs · {legs.length}
        </Eyebrow>
        <span style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textDim, letterSpacing: "0.18em", textTransform: "uppercase" }}>tap a row for reasoning</span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: LEG_GRID,
          gap: SP[4],
          padding: `${SP[3]}px ${SP[5]}px`,
          borderBottom: `1px solid ${SW.border}`,
          fontFamily: SW.mono,
          fontSize: 9.5,
          color: SW.textMuted,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 500,
        }}
      >
        <span>#</span>
        <span>Fixture</span>
        <span>Competition</span>
        <span>Kickoff</span>
        <span>Pick</span>
        <span style={{ textAlign: "center" }}>Conf.</span>
        <span style={{ textAlign: "right" }}>Odds</span>
      </div>

      {legs.map((leg, i) => {
        const isExpanded = expanded === i;
        return (
          <div
            key={`${leg.fixture_id}-${i}`}
            data-testid={`signals-legs-row-${i}`}
            onClick={() => onToggle(i)}
            style={{ cursor: "pointer", background: isExpanded ? SW.surface2 : "transparent", transition: "background .15s ease" }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: LEG_GRID,
                gap: SP[4],
                padding: `${SP[4]}px ${SP[5]}px`,
                borderBottom: i === legs.length - 1 ? "none" : `1px solid ${SW.border}`,
                alignItems: "center",
              }}
            >
              <span style={{ fontFamily: SW.mono, fontSize: 11, color: SW.textDim, letterSpacing: "0.1em" }}>0{i + 1}</span>
              <div data-testid={`signals-legs-row-${i}-fixture`} style={{ minWidth: 0 }}>
                <div style={{ fontFamily: SW.serif, fontSize: 16, fontWeight: 500, color: SW.text, lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {leg.home_team} <span style={{ color: SW.textDim, fontStyle: "italic" }}>v</span> {leg.away_team}
                </div>
              </div>
              <div
                data-testid={`signals-legs-row-${i}-competition`}
                style={{ fontFamily: SW.mono, fontSize: 10.5, color: SW.textSec, letterSpacing: "0.14em", textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {leg.competition}
              </div>
              <div data-testid={`signals-legs-row-${i}-kickoff`} style={{ fontFamily: SW.mono, fontSize: 10.5, color: SW.textSec, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                {fmtKickoff(leg.kickoff_at)}
              </div>
              <div data-testid={`signals-legs-row-${i}-pick`} style={{ minWidth: 0 }}>
                <span style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, letterSpacing: "0.16em", textTransform: "uppercase", marginRight: SP[2] }}>
                  {marketLabel(leg.market)}
                </span>
                <span style={{ fontFamily: SW.serif, fontStyle: "italic", fontSize: 14, color: SW.accent }}>{selectionLabel(leg)}</span>
              </div>
              <div data-testid={`signals-legs-row-${i}-confidence`} style={{ display: "flex", justifyContent: "center" }}>
                <Conf high={leg.confidence === "high"} />
              </div>
              <div
                data-testid={`signals-legs-row-${i}-odds`}
                style={{ fontFamily: SW.serif, fontSize: 24, fontWeight: 500, color: SW.text, textAlign: "right", lineHeight: 1, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}
              >
                {leg.odds.toFixed(2)}
              </div>
            </div>

            {isExpanded && leg.reason && (
              <div style={{ padding: `0 ${SP[5]}px ${SP[5]}px ${SP[5] + 36 + SP[4]}px`, borderBottom: i === legs.length - 1 ? "none" : `1px solid ${SW.border}` }}>
                <div
                  data-testid={`signals-legs-row-${i}-reason`}
                  style={{ paddingLeft: SP[4], borderLeft: `1px solid ${SW.accentDim}`, fontFamily: SW.serif, fontStyle: "italic", fontSize: 14, color: SW.textSec, lineHeight: 1.6, maxWidth: 700 }}
                >
                  “{leg.reason}”
                </div>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

// ─── House notes ───────────────────────────────────────────────────
function HouseNotes({ notes }: { notes: string[] }): JSX.Element | null {
  if (!notes.length) return null;
  return (
    <section data-testid="signals-house-notes" style={{ ...cardStyle, marginBottom: SP[6], padding: `${SP[5]}px ${SP[6]}px` }}>
      <div style={{ display: "flex", alignItems: "center", gap: SP[3], marginBottom: SP[4] }}>
        <span
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            border: `1px solid ${SW.warn}`,
            background: SW.warnInk,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: SW.warn,
            fontFamily: SW.serif,
            fontStyle: "italic",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          !
        </span>
        <Eyebrow tone="text">House notes</Eyebrow>
        <span style={{ flex: 1, height: 1, background: SW.border }} />
        <span style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textDim, letterSpacing: "0.18em", textTransform: "uppercase" }}>shown alongside upside · binding</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: notes.length > 1 ? "1fr 1fr" : "1fr", gap: SP[4] }}>
        {notes.map((n, i) => (
          <div key={i} data-testid={`signals-house-notes-item-${i}`} style={{ fontFamily: SW.sans, fontSize: 14, color: SW.textSec, lineHeight: 1.6, paddingLeft: SP[4], position: "relative" }}>
            <span style={{ position: "absolute", left: 0, top: 10, width: 12, height: 1, background: SW.warn }} />
            {n}
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Bench ─────────────────────────────────────────────────────────
function Bench({ pool, onSwap }: { pool: SignalLeg[]; onSwap: (fixtureId: string) => void }): JSX.Element | null {
  if (!pool.length) return null;
  const visible = pool.slice(0, 6);
  return (
    <section data-testid="signals-bench" style={{ ...cardStyle, marginBottom: SP[6], overflow: "hidden" }}>
      <div style={{ padding: `${SP[4]}px ${SP[5]}px`, borderBottom: `1px solid ${SW.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Eyebrow tone="muted" line>
          Bench · swap a leg
        </Eyebrow>
        <span style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, letterSpacing: "0.18em", textTransform: "uppercase" }}>{pool.length} priced fixtures</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        {visible.map((p, i) => (
          <div
            key={`${p.fixture_id}-${i}`}
            data-testid={`signals-bench-row-${i}`}
            style={{
              padding: `${SP[4]}px ${SP[5]}px`,
              borderRight: i % 2 === 0 ? `1px solid ${SW.border}` : "none",
              borderBottom: i < visible.length - 2 ? `1px solid ${SW.border}` : "none",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto auto",
              gap: SP[3],
              alignItems: "center",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: SW.sans, fontSize: 13, fontWeight: 500, color: SW.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.home_team} <span style={{ color: SW.textDim }}>v</span> {p.away_team}
              </div>
              <div style={{ fontFamily: SW.mono, fontSize: 9.5, color: SW.textMuted, marginTop: 3, letterSpacing: "0.16em", textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.competition} · {marketLabel(p.market)} <span style={{ color: SW.accent }}>{selectionLabel(p)}</span>
              </div>
            </div>
            <Conf high={p.confidence === "high"} />
            <div style={{ display: "flex", alignItems: "center", gap: SP[3] }}>
              <span style={{ fontFamily: SW.serif, fontSize: 16, fontWeight: 500, color: SW.text, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{p.odds.toFixed(2)}</span>
              <button
                type="button"
                data-testid={`signals-bench-row-${i}-swap`}
                onClick={() => onSwap(p.fixture_id)}
                title="Swap into slip"
                style={{
                  width: 26,
                  height: 26,
                  border: `1px solid ${SW.borderBright}`,
                  background: "transparent",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontFamily: SW.mono,
                  fontSize: 14,
                  color: SW.textMuted,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>

      {pool.length > 6 && (
        <div data-testid="signals-bench-more" style={{ padding: `${SP[3]}px ${SP[5]}px`, borderTop: `1px solid ${SW.border}`, textAlign: "center", fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, letterSpacing: "0.2em", textTransform: "uppercase" }}>
          + {pool.length - 6} more on the bench
        </div>
      )}
    </section>
  );
}

// ─── Empty state ───────────────────────────────────────────────────
function EmptyState({ onTrySuggested }: { onTrySuggested: (k: WindowKind) => void }): JSX.Element {
  const suggestions: { k: WindowKind; label: string; sub: string }[] = [
    { k: "weekend", label: "Weekend", sub: "Fri–Sun" },
    { k: "7d", label: "7 days", sub: "next week" },
    { k: "today", label: "Today", sub: "tonight" },
  ];
  return (
    <section data-testid="signals-empty" style={{ ...cardStyle, marginBottom: SP[6], padding: `${SP[7]}px ${SP[6]}px` }}>
      <svg data-testid="signals-empty-horizon" width="100%" height="80" viewBox="0 0 800 80" preserveAspectRatio="none" style={{ display: "block", marginBottom: SP[5] }} aria-hidden="true">
        {Array.from({ length: 60 }).map((_, i) => {
          const x = (i / 59) * 800;
          const tall = i < 18;
          const h = tall ? 14 + ((i * 13) % 30) : 3;
          return <line key={i} x1={x} y1={64 - h} x2={x} y2={64} stroke={tall ? SW.accent : SW.textDim} strokeWidth="1.2" opacity={tall ? 1 : 0.5} />;
        })}
        <line x1="0" y1="64" x2="800" y2="64" stroke={SW.borderBright} strokeWidth="1" />
        <line x1={(18 / 59) * 800} y1="12" x2={(18 / 59) * 800} y2="74" stroke={SW.accent} strokeWidth="1" strokeDasharray="2 3" />
        <text x={(18 / 59) * 800 + 8} y="20" fontFamily="JetBrains Mono" fontSize="10" fill={SW.accent} letterSpacing="0.16em">
          14d PRICED HORIZON
        </text>
      </svg>

      <Eyebrow tone="amber" line>
        Honest blank
      </Eyebrow>
      <div style={{ fontFamily: SW.serif, fontSize: 36, fontWeight: 500, color: SW.text, lineHeight: 1.1, letterSpacing: "-0.02em", marginTop: SP[4], maxWidth: 680 }}>
        No qualifying composition. Fixtures only price <span style={{ fontStyle: "italic", color: SW.accent }}>~14 days</span> out — the rest of this window is genuinely thin.
      </div>
      <div style={{ fontFamily: SW.sans, fontSize: 14, color: SW.textSec, marginTop: SP[4], lineHeight: 1.6, maxWidth: 640 }}>
        We could pad the slip with speculative lines, but you'd be reading fiction. Try a real window:
      </div>

      <div style={{ marginTop: SP[5], display: "flex", gap: SP[3], flexWrap: "wrap" }}>
        {suggestions.map((w) => (
          <button
            key={w.k}
            type="button"
            data-testid={`signals-empty-suggested-${w.k}`}
            onClick={() => onTrySuggested(w.k)}
            style={{ padding: `${SP[4]}px ${SP[5]}px`, background: SW.accentInk, border: `1px solid ${SW.accentDim}`, borderRadius: 4, cursor: "pointer", textAlign: "left" }}
          >
            <div style={{ fontFamily: SW.serif, fontSize: 18, fontWeight: 500, color: SW.accent, lineHeight: 1 }}>Try {w.label.toLowerCase()} →</div>
            <div style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, letterSpacing: "0.14em", textTransform: "uppercase", marginTop: SP[2] }}>{w.sub}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ─── Refine rail ───────────────────────────────────────────────────
function RefineRail({
  applied,
  commandText,
  onCommandChange,
  onSubmit,
  onChip,
  onRemove,
}: {
  applied: string[];
  commandText: string;
  onCommandChange: (v: string) => void;
  onSubmit: () => void;
  onChip: (cmd: string) => void;
  onRemove: (cmd: string) => void;
}): JSX.Element {
  return (
    <aside
      data-testid="signals-refine"
      style={{ width: RAIL_W, flexShrink: 0, position: "sticky", top: SP[5], alignSelf: "start", ...cardStyle, overflow: "hidden" }}
    >
      <div style={{ padding: `${SP[5]}px ${SP[5]}px ${SP[4]}px`, borderBottom: `1px solid ${SW.border}` }}>
        <Eyebrow tone="amber" line>
          Refine
        </Eyebrow>
        <div style={{ marginTop: SP[3], fontFamily: SW.serif, fontSize: 18, fontWeight: 500, color: SW.text, lineHeight: 1.3 }}>
          One living slip.
          <br />
          <span style={{ fontStyle: "italic", color: SW.textMuted }}>Mutate it, don't restart.</span>
        </div>
      </div>

      <div style={{ padding: `${SP[5]}px ${SP[5]}px ${SP[3]}px`, display: "flex", flexDirection: "column", gap: SP[4] }}>
        {/* Applied */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP[3] }}>
            <span style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 500 }}>Applied</span>
            <span style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textDim, letterSpacing: "0.18em", textTransform: "uppercase" }}>reversible</span>
          </div>

          {applied.length > 0 ? (
            <div data-testid="signals-refine-applied" style={{ display: "flex", flexDirection: "column", gap: SP[2] }}>
              {applied.map((cmd, i) => (
                <div
                  key={`${cmd}-${i}`}
                  data-testid={`signals-refine-applied-item-${i}`}
                  style={{ display: "grid", gridTemplateColumns: "1fr 22px", gap: SP[3], alignItems: "center", padding: SP[3], background: SW.surface2, border: `1px solid ${SW.borderBright}`, borderRadius: 4, animation: "sw-fade .25s ease" }}
                >
                  <span style={{ fontFamily: SW.serif, fontStyle: "italic", fontSize: 13.5, color: SW.text, lineHeight: 1.3 }}>“{cmd}”</span>
                  <button
                    type="button"
                    data-testid={`signals-refine-applied-item-${i}-remove`}
                    onClick={() => onRemove(cmd)}
                    title="Remove this refinement"
                    style={{ color: SW.textMuted, background: "transparent", border: "none", cursor: "pointer", fontFamily: SW.mono, fontSize: 14, padding: 0, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div data-testid="signals-refine-empty" style={{ padding: `${SP[3]}px ${SP[4]}px`, border: `1px dashed ${SW.borderHi}`, borderRadius: 4, fontFamily: SW.serif, fontStyle: "italic", fontSize: 12.5, color: SW.textMuted, lineHeight: 1.5 }}>
              No refines yet. Try a chip below, or type a command.
            </div>
          )}
        </div>

        {/* Quick chips */}
        <div>
          <div style={{ fontFamily: SW.mono, fontSize: 10, color: SW.textMuted, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 500, marginBottom: SP[3] }}>Try</div>
          <div data-testid="signals-refine-chip-row" style={{ display: "flex", flexWrap: "wrap", gap: SP[2] }}>
            {QUICK_CMDS.map((q) => (
              <button
                key={q}
                type="button"
                data-testid={`signals-refine-chip-${q.replace(/\s+/g, "-")}`}
                onClick={() => onChip(q)}
                style={{ padding: "7px 11px", borderRadius: 999, border: `1px solid ${SW.borderBright}`, background: "transparent", fontFamily: SW.sans, fontSize: 12, color: SW.textSec, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Command bar */}
      <form
        data-testid="signals-refine-command-form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          onSubmit();
        }}
        style={{ padding: SP[5], borderTop: `1px solid ${SW.border}` }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: SP[3],
            padding: `${SP[3]}px ${SP[4]}px`,
            background: SW.surface2,
            border: `1px solid ${commandText ? SW.accent : SW.borderBright}`,
            borderRadius: 4,
            boxShadow: commandText ? `0 0 0 3px ${SW.accentInk}` : "none",
            transition: "all .2s ease",
          }}
        >
          <span style={{ color: SW.accent, fontFamily: SW.mono, fontSize: 14, lineHeight: 1 }}>›</span>
          <input
            data-testid="signals-refine-command-input"
            value={commandText}
            onChange={(e) => onCommandChange(e.target.value)}
            placeholder="tell the engine…"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontFamily: SW.serif, fontStyle: "italic", fontSize: 14, color: SW.text, minWidth: 0 }}
          />
          <button
            type="submit"
            data-testid="signals-refine-command-submit"
            disabled={!commandText.trim()}
            style={{
              padding: "4px 10px",
              border: "none",
              background: commandText.trim() ? SW.accent : "transparent",
              color: commandText.trim() ? SW.bg : SW.textMuted,
              fontFamily: SW.mono,
              fontSize: 9.5,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 600,
              cursor: commandText.trim() ? "pointer" : "default",
              borderRadius: 2,
            }}
          >
            ↵ send
          </button>
        </div>
        <div style={{ marginTop: SP[3], textAlign: "center", fontFamily: SW.serif, fontStyle: "italic", fontSize: 11.5, color: SW.textMuted }}>could reach — never will reach.</div>
      </form>
    </aside>
  );
}

// ─── Overlays ──────────────────────────────────────────────────────
function ComposingOverlay({ active }: { active: boolean }): JSX.Element | null {
  if (!active) return null;
  return (
    <div
      data-testid="signals-composing-overlay"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(10,10,10,0.86)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 20,
        animation: "sw-fade .25s ease",
        borderRadius: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: SP[3], marginBottom: SP[5] }}>
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: SW.accent, boxShadow: `0 0 0 6px ${SW.accentInk}`, animation: "sw-pulse 1.2s ease-in-out infinite" }} />
        <span style={{ fontFamily: SW.mono, fontSize: 11, color: SW.accent, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 500 }}>Composing</span>
      </div>
      <div style={{ fontFamily: SW.serif, fontSize: 28, fontWeight: 500, color: SW.text, lineHeight: 1.25, letterSpacing: "-0.015em", textAlign: "center", maxWidth: 500, padding: `0 ${SP[5]}px` }}>
        Scanning fixtures · ranking candidates · composing the safest reach.
      </div>
    </div>
  );
}

function Toast({ toast }: { toast: { text: string; accent?: boolean } | null }): JSX.Element | null {
  if (!toast) return null;
  return (
    <div
      data-testid="signals-toast"
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        padding: "11px 18px",
        background: SW.surface2,
        border: `1px solid ${toast.accent ? SW.accent : SW.borderBright}`,
        color: toast.accent ? SW.accent : SW.text,
        fontFamily: SW.mono,
        fontSize: 11,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        zIndex: 1000,
        boxShadow: "0 12px 30px rgba(0,0,0,0.5)",
        animation: "sw-toast .25s ease",
        borderRadius: 4,
      }}
    >
      {toast.text}
    </div>
  );
}

const KEYFRAMES = `
@keyframes sw-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.85); } }
@keyframes sw-fade  { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@keyframes sw-toast { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }
`;

// ─── Page ──────────────────────────────────────────────────────────
export function SignalsPage(): JSX.Element {
  const { result, loading, refining, error, create, refine } = useSignalSearch();
  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: false });

  const [windowKey, setWindowKey] = useState<WindowKind>("7d");
  const [target, setTarget] = useState<number>(5.0);
  const [maxLegs, setMaxLegs] = useState<number>(4);
  const [posture, setPosture] = useState<Posture>("current");
  const [expanded, setExpanded] = useState<number>(-1);
  const [cmd, setCmd] = useState<string>("");
  const [toast, setToast] = useState<{ text: string; accent?: boolean } | null>(null);

  const toastT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recomposeT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ranOnce = useRef(false);
  // Latest intent — the (debounced) recompose reads refs so it never fires
  // with stale values, and we update state separately for instant display.
  const windowRef = useRef<WindowKind>(windowKey);
  const targetRef = useRef<number>(target);
  const maxLegsRef = useRef<number>(maxLegs);

  const tellToast = (text: string, accent = true): void => {
    setToast({ text, accent });
    if (toastT.current) clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToast(null), 1900);
  };

  const doCompose = useCallback((): void => {
    setPosture("current");
    setExpanded(-1);
    void create({
      window_kind: windowRef.current,
      target_odds: targetRef.current,
      max_legs: maxLegsRef.current,
    });
  }, [create]);

  // Coalesce rapid intent changes into a single network recompose. The
  // controls stay live and the current slip stays on screen meanwhile —
  // no full-screen blanking on every tweak.
  const scheduleRecompose = (delay = 280): void => {
    if (recomposeT.current) clearTimeout(recomposeT.current);
    recomposeT.current = setTimeout(() => doCompose(), delay);
  };

  // Auto-compose once on mount so the page lands live (pure selector, no LLM).
  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;
    doCompose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (toastT.current) clearTimeout(toastT.current);
    if (recomposeT.current) clearTimeout(recomposeT.current);
  }, []);

  const onWindow = (k: WindowKind): void => {
    setWindowKey(k);
    windowRef.current = k;
    scheduleRecompose(0);
  };
  // Drag = visual only (instant); commit (release / tick click) recomposes.
  const onTargetDrag = (v: number): void => {
    setTarget(v);
    targetRef.current = v;
  };
  const onTargetCommit = (v: number): void => {
    setTarget(v);
    targetRef.current = v;
    scheduleRecompose(0);
  };
  const onMaxLegs = (delta: number): void => {
    const next = Math.max(2, Math.min(12, maxLegsRef.current + delta));
    setMaxLegs(next);
    maxLegsRef.current = next;
    scheduleRecompose(380);
  };
  const onChip = (text: string): void => {
    const c = text.trim();
    if (!c) return;
    void refine(c);
    tellToast(`+ ${c}`);
  };
  const onSubmitCmd = (): void => {
    onChip(cmd);
    setCmd("");
  };
  // Reversible refine + bench swap (server is the source of truth via the
  // command stack: swap pushes "swap in <id>", × pushes "undo: <cmd>").
  const onAppliedRemove = (c: string): void => {
    void refine(`undo: ${c}`);
    tellToast("Removed", false);
  };
  const onBenchSwap = (fixtureId: string): void => {
    void refine(`swap in ${fixtureId}`);
    tellToast("Swapped into slip");
  };

  const composing = loading || refining;
  // Full-screen overlay/dimming ONLY on the very first load (no slip yet).
  // Subsequent recomposes keep the current slip visible and update in place.
  const initialLoading = loading && !result;
  const rendered = pickSlip(result, posture);
  const composedTarget = result?.target_odds ?? target;
  const shortfall = !!rendered && rendered.combined_odds < composedTarget;
  const highVariance = !!rendered && rendered.estimated_probability < 0.1;
  const notes = mergeNotes(rendered, result);

  return (
    <div style={{ color: SW.text, fontFamily: SW.sans, fontVariantNumeric: "tabular-nums" }}>
      <style>{KEYFRAMES}</style>

      <PageHeader queryId={result?.query_id} live={composing} />

      <div style={{ display: "grid", gridTemplateColumns: `minmax(0, 1fr) ${RAIL_W}px`, gap: SP[6], alignItems: "start" }}>
        {/* Main column */}
        <main style={{ position: "relative", minWidth: 0 }}>
          <IntentBar
            windowKey={windowKey}
            target={target}
            maxLegs={maxLegs}
            dimmed={initialLoading}
            onWindow={onWindow}
            onTargetDrag={onTargetDrag}
            onTargetCommit={onTargetCommit}
            onMaxLegs={onMaxLegs}
            onRecompose={() => scheduleRecompose(0)}
          />

          {error && !result && (
            <section style={{ ...cardStyle, marginBottom: SP[6], padding: `${SP[6]}px`, borderTop: `2px solid ${SW.danger}` }}>
              <Eyebrow tone="muted" line>
                Search failed
              </Eyebrow>
              <div style={{ marginTop: SP[3], fontFamily: SW.serif, fontSize: 22, color: SW.text }}>Couldn't reach the composer.</div>
              <div style={{ marginTop: SP[2], fontFamily: SW.sans, fontSize: 13, color: SW.textSec }}>{error}</div>
            </section>
          )}

          {result && !rendered && (
            <>
              <EmptyState onTrySuggested={onWindow} />
              <DoctrineSection
                probabilityPicks={doctrineProbPicks}
                valuePicks={doctrineValuePicks}
                isFullFallback
                substrateEmpty={substrateEmpty}
                substrateWeek={substrateWeek}
              />
            </>
          )}

          {result && rendered && (
            <>
              <FunnelStrip
                scanned={result.candidates_summary.total_scanned}
                qualified={result.candidates_summary.qualified}
                selected={rendered.leg_count}
                rejected={result.candidates_summary.rejected}
                target={composedTarget}
              />
              <HeroSlip slip={rendered} target={composedTarget} shortfall={shortfall} highVariance={highVariance} posture={posture} />
              <PostureSwitch posture={posture} onChange={(p) => setPosture(p)} result={result} />
              <LegsTable legs={rendered.legs} expanded={expanded} onToggle={(i) => setExpanded(expanded === i ? -1 : i)} />
              <HouseNotes notes={notes} />
              <Bench pool={result.candidate_pool ?? []} onSwap={onBenchSwap} />
            </>
          )}

          <ComposingOverlay active={initialLoading} />
        </main>

        {/* Refine rail */}
        <RefineRail
          applied={result?.command_stack ?? []}
          commandText={cmd}
          onCommandChange={setCmd}
          onSubmit={onSubmitCmd}
          onChip={onChip}
          onRemove={onAppliedRemove}
        />
      </div>

      <Toast toast={toast} />
    </div>
  );
}
